
// context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { hasAdminRole, getUserRoles } from '../utils/auth';
import OnboardingForm from '../components/OnboardingForm';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isAdmin, setIsAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Google OAuth state
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(true);

  const getAccessToken = useCallback(() => {
    return googleAccessToken;
  }, [googleAccessToken]);

  const fetchUserProfile = async () => {
    if (!isAuthenticated || profileLoading) return;

    setProfileLoading(true);
    try {
      const token = getAccessToken();
      const apiClient = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const response = await apiClient.get('/auth/me');
      const userProfileData = response.data.user;
      setUserProfile(userProfileData);

      // Check if profile is complete
      const isComplete = isProfileComplete(userProfileData);
      if (!isComplete && !userProfileData.hasUpdatedDetails) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);

      // If unauthorized, log user out
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Authentication failed while fetching profile, logging out...');
        handleLogout();
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const isProfileComplete = (profile) => {
    if (!profile) return false;
    const requiredFields = ['city', 'address', 'country', 'contactDetailsValue'];
    return requiredFields.every(field =>
      profile[field] && profile[field].toString().trim() !== ''
    );
  };

  const handleOnboardingSubmit = async (formData) => {
    try {
      const token = getAccessToken();

      // Determine if we're dealing with FormData (file uploads) or regular JSON
      const isFormData = formData instanceof FormData;

      const apiClient = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        },
      });

      const response = await apiClient.put('/auth/me', formData);
      setUserProfile(response.data.user);
      setShowOnboarding(false);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
  };

  const showOnboardingModal = () => {
    setShowOnboarding(true);
  };

  // ========================================
  // Google OAuth Functions
  // ========================================

  /**
   * Clear Google authentication data
   */
  const clearGoogleAuth = useCallback(() => {
    localStorage.removeItem('google_access_token');
    localStorage.removeItem('google_token_expiry');
    localStorage.removeItem('google_user_info');
    localStorage.removeItem('google_refresh_token');
    setGoogleAccessToken(null);
    setGoogleUserInfo(null);
    setIsGoogleAuthenticated(false);
  }, []);

  /**
   * Refresh the Google access token using the refresh token
   */
  const refreshGoogleAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('google_refresh_token');

    if (!refreshToken) {
      console.log('No Google refresh token available');
      return false;
    }

    try {
      console.log('🔄 Refreshing Google access token...');

      // Call backend to refresh token
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Token refresh failed');
      }

      // Support both camelCase (backend format) and snake_case (legacy)
      const newToken = data.googleAccessToken || data.access_token;
      const expiresIn = data.expiresIn || data.expires_in;
      const expiryTime = new Date().getTime() + (expiresIn * 1000);

      // Store new access token
      localStorage.setItem('google_access_token', newToken);
      localStorage.setItem('google_token_expiry', expiryTime.toString());

      setGoogleAccessToken(newToken);
      setIsGoogleAuthenticated(true);

      console.log('✅ Google access token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Error refreshing Google token:', error);
      // Refresh token is invalid or expired, need to re-authenticate
      clearGoogleAuth();
      toast.warning('Google session expired. Please log in again.');
      return false;
    }
  }, [clearGoogleAuth]);

  /**
   * Login to Google
   */
  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        setIsGoogleLoading(true);

        // Validate that the user granted all required scopes
        const REQUIRED_SCOPES = [
          'https://www.googleapis.com/auth/drive.file',
          'https://www.googleapis.com/auth/drive.readonly',
          'https://www.googleapis.com/auth/documents',
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/userinfo.profile',
        ];
        const grantedScopes = (codeResponse.scope || '').split(' ');
        const missingScopes = REQUIRED_SCOPES.filter(s => !grantedScopes.includes(s));
        if (missingScopes.length > 0) {
          toast.error(
            'All permissions are required to use this app. Please try signing in again and check all the boxes. Missing: ' +
            missingScopes.map(s => s.split('/').pop()).join(', ')
          );
          setIsGoogleLoading(false);
          return;
        }

        console.log('🔐 Exchanging Google authorization code for tokens...');
//Todo swith back to /api/ -- below before deployment
        // Exchange code for tokens via backend
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: codeResponse.code
          })
        });

        const data = await response.json();
        console.log('Response from backend:', data);

        if (!data.success) {
          throw new Error(data.error || 'Token exchange failed');
        }

        // Extract data from backend response (using camelCase field names)
        const googleToken = data.access_token;
        const googleRefreshToken = data.refresh_token;
        const auth0Token = data.access_token;
        const userData = data.user;
        const googleUserData = data.googleUserInfo;

        console.log('✅ Tokens received from backend');

        // Store Google token with expiry
        const expiryTime = new Date().getTime() + (data.expiresIn * 1000);
        localStorage.setItem('google_access_token', googleToken);
        localStorage.setItem('google_token_expiry', expiryTime.toString());

        // Store Google refresh token if provided
        if (googleRefreshToken) {
          localStorage.setItem('google_refresh_token', googleRefreshToken);
          console.log('✅ Google refresh token stored');
        }

        // Store Google token (required for API calls)
        if (auth0Token) {
          localStorage.setItem('accessToken', auth0Token);
          console.log('✅ Google token stored');
        }

        // Store Google user data
        if (userData) {
          localStorage.setItem('auth0_user', JSON.stringify(userData));
          setIsAdmin(userData.isAdmin);
        }
        setAdminLoading(false);

        setGoogleAccessToken(googleToken);
        setIsGoogleAuthenticated(true);

        // Set authenticated state
        setIsAuthenticated(true);
        setUser(googleUserData);

        // Use Google user info from backend response (no need to fetch again)
        if (googleUserData) {
          setGoogleUserInfo(googleUserData);
          localStorage.setItem('google_user_info', JSON.stringify(googleUserData));
          
        }

        // Success message
        const userEmail = userData?.email || googleUserData?.email;
        toast.success(`Signed in as ${userEmail}`);

        const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
        if (postLoginRedirect) {
          sessionStorage.removeItem('post_login_redirect');
        }

        // After successful Google login, redirect to the intended page if one was set.
        // The app will pick up the session from localStorage on page load.
        console.log('✅ Authentication successful, redirecting...');
        window.location.href = postLoginRedirect || '/translator/dashboard';
      } catch (error) {
        console.error('Error during Google login:', error);
        toast.error(`Failed to complete login: ${error.message}`);
        clearGoogleAuth();
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
      toast.error('Google login failed');
      setIsGoogleLoading(false);
    },
    scope: [
      'https://www.googleapis.com/auth/drive.file',
      'https://www.googleapis.com/auth/drive.readonly',  // Required for reading comments from Google Drive
      'https://www.googleapis.com/auth/documents',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/userinfo.profile'
    ].join(' '),
    prompt: 'consent',
    flow: 'auth-code'
  });

  /**
   * Revoke Google OAuth token
   */
  const revokeGoogleToken = async (token) => {
    const response = await fetch('https://oauth2.googleapis.com/revoke', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `token=${token}`,
    });

    if (!response.ok) {
      throw new Error(`Token revocation failed: ${response.status} ${response.statusText}`);
    }

    return response;
  };

  /**
   * Logout from Google with proper token revocation
   */
  const googleLogout = useCallback(async () => {
    // Revoke token with Google before clearing local storage
    if (googleAccessToken) {
      try {
        await revokeGoogleToken(googleAccessToken);
        console.log('✅ Google token revoked successfully');
      } catch (error) {
        console.error('⚠️ Failed to revoke Google token:', error);
        // Continue with logout even if revocation fails
      }
    }

    clearGoogleAuth();
    toast.info('Disconnected from Google');
  }, [googleAccessToken, clearGoogleAuth]);

  const handleLogin = useCallback(() => {
    googleLogin();
  }, [googleLogin]);

  const handleLogout = useCallback(() => {
    googleLogout();
    setIsAuthenticated(false);
    setUser(null);
  }, [googleLogout]);

  /**
   * Ensure Google token is valid, refresh if needed
   */
  const ensureValidGoogleToken = useCallback(async () => {
    const tokenExpiry = localStorage.getItem('google_token_expiry');

    if (!tokenExpiry || new Date().getTime() >= parseInt(tokenExpiry)) {
      // Token expired, try to refresh
      console.log('Google token expired, attempting refresh...');
      return await refreshGoogleAccessToken();
    }

    return true;
  }, [refreshGoogleAccessToken]);

  // Check for existing Google token in localStorage on mount
  useEffect(() => {
    const initGoogleAuth = async () => {
      try {
        const storedToken = localStorage.getItem('google_access_token');
        const storedUserInfo = localStorage.getItem('google_user_info');
        const tokenExpiry = localStorage.getItem('google_token_expiry');
        const refreshToken = localStorage.getItem('google_refresh_token');

        if (storedToken && tokenExpiry) {
          // Check if token is still valid
          if (new Date().getTime() < parseInt(tokenExpiry)) {
            // Token is still valid
            setGoogleAccessToken(storedToken);
            setIsGoogleAuthenticated(true);
            setIsAuthenticated(true);
            if (storedUserInfo) {
              const userInfo = JSON.parse(storedUserInfo);
              setGoogleUserInfo(userInfo);
              setUser(userInfo);
            }
            const storedUser = localStorage.getItem('auth0_user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              setIsAdmin(userData.isAdmin ?? false);
            }
            setAdminLoading(false);
            console.log('✅ Restored valid Google session from localStorage');
          } else if (refreshToken) {
            // Token expired but we have refresh token, try to refresh
            console.log('🔄 Google token expired on mount, attempting refresh...');
            const refreshed = await refreshGoogleAccessToken();
            if (refreshed && storedUserInfo) {
              const userInfo = JSON.parse(storedUserInfo);
              setGoogleUserInfo(userInfo);
              setUser(userInfo);
              setIsAuthenticated(true);
              const storedUser = localStorage.getItem('auth0_user');
              if (storedUser) {
                const userData = JSON.parse(storedUser);
                setIsAdmin(userData.isAdmin ?? false);
              }
              setAdminLoading(false);
            }
          } else {
            // Token expired and no refresh token, clear storage
            console.log('Google token expired and no refresh token available');
            clearGoogleAuth();
            setAdminLoading(false);
          }
        } else {
          // No stored token, user is not authenticated
          setAdminLoading(false);
        }
      } finally {
        setIsGoogleLoading(false);
        setIsLoading(false);
      }
    };

    initGoogleAuth();
  }, [refreshGoogleAccessToken, clearGoogleAuth]);


  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Pendo when we have a logged-in user and (optionally) the user profile
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    try {
      const apiKey = process.env.REACT_APP_PENDO_KEY || null;
      // If the Pendo script was added directly to public/index.html, window.pendo may still be
      // a queued stub that accepts initialize calls. We guard to run initialize once.
      if (typeof window !== 'undefined' && window.pendo && !window.pendo.__initialized) {
        const visitor = {
          id: user.sub || user.id || user.email || 'unknown',
          email: user.email || undefined,
          full_name: user.name || undefined,
        };

        // Try to pick an account id from userProfile if available, else fall back to a default
        const accountId = (userProfile && (userProfile.accountId || userProfile.tenantId || userProfile.orgId)) || 'default-account';

        const account = {
          id: accountId,
          accountName: (userProfile && (userProfile.accountName || userProfile.organizationName)) || undefined,
        };

        // If API key is present in env, prefer loading via that; otherwise assume global script already loaded
        if (apiKey) {
          // If the global pendo was not loaded via CDN yet, ensure the correct static URL is used
          // but since we injected the script in public/index.html, calling initialize is sufficient.
        }

        try {
          window.pendo.initialize({ visitor, account });
        } catch (e) {
          // pendo may be a queued stub; push initialize to its queue if available
          try {
            window.pendo && window.pendo._q && window.pendo._q.unshift && window.pendo._q.unshift(['initialize', { visitor, account }]);
          } catch (err) {
            console.warn('Pendo initialization failed:', err);
          }
        }

        // mark initialized to avoid duplicate calls
        window.pendo.__initialized = true;
      }
    } catch (err) {
      console.error('Error initializing Pendo:', err);
    }
  }, [isAuthenticated, user, userProfile]);

  const value = useMemo(() => ({
    // Auth properties
    currentUser: user,
    user,
    userProfile,
    isAuthenticated,
    loading: isLoading,
    profileLoading,
    login: handleLogin,
    logout: handleLogout,
    getAccessToken,
    isAdmin,
    userRoles,
    adminLoading,
    fetchUserProfile,
    handleOnboardingSubmit,
    handleOnboardingClose,
    showOnboardingModal,
    isProfileComplete: userProfile ? isProfileComplete(userProfile) : false,

    // Google OAuth properties
    googleAccessToken,
    googleUserInfo,
    isGoogleAuthenticated,
    isGoogleLoading,
    googleLogin,
    googleLogout,
    ensureValidGoogleToken,
  }), [
    user,
    userProfile,
    isAuthenticated,
    isLoading,
    profileLoading,
    handleLogin,
    handleLogout,
    getAccessToken,
    isAdmin,
    userRoles,
    adminLoading,
    fetchUserProfile,
    handleOnboardingSubmit,
    handleOnboardingClose,
    showOnboardingModal,
    googleAccessToken,
    googleUserInfo,
    isGoogleAuthenticated,
    isGoogleLoading,
    googleLogin,
    googleLogout,
    ensureValidGoogleToken,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showOnboarding && (
        <OnboardingForm
          isOpen={showOnboarding}
          onClose={handleOnboardingClose}
          onSubmit={handleOnboardingSubmit}
          initialData={userProfile || {}}
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
